import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getStatements, getStatement } from "@/lib/api";
import { ArrowLeft } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { ArticleJsonLd } from "@/components/JsonLd";
import { useT } from "@/lib/t";

export function Statements() {
  const t = useT();
  const { lang } = useLang();
  const [items, setItems] = useState([]);
  const [type, setType] = useState("All");
  const [q, setQ] = useState("");

  useEffect(() => {
    const params = { lang };
    if (type !== "All") params.type = type;
    if (q) params.q = q;
    getStatements(params).then(setItems);
  }, [type, q, lang]);

  const TYPES = ["All", "Statement", "Bishop Letter", "Testimony"];

  return (
    <div data-testid="statements-library">
      <section className="bg-ccc-plum text-ccc-paper py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="eyebrow eyebrow-light mb-3">{t("statements.eyebrow")}</div>
          <h1 className="font-serif text-5xl mb-4">{t("statements.hero_title")}</h1>
          <p className="text-ccc-paper/85 max-w-2xl" style={{ fontSize: "18px", lineHeight: 1.7 }}>
            {t("statements.hero_body")}
          </p>
        </div>
      </section>
      <section className="max-w-6xl mx-auto px-6 py-10">
        <div className="grid md:grid-cols-3 gap-3 mb-8">
          <input placeholder={t("statements.search_placeholder")} value={q} onChange={e => setQ(e.target.value)}
            data-testid="statements-search"
            className="md:col-span-2 px-4 py-3 border border-ccc-rule focus:outline-none focus:border-ccc-plum bg-white"/>
          <select value={type} onChange={e => setType(e.target.value)}
            data-testid="statements-type"
            className="px-3 py-3 border border-ccc-rule bg-white focus:outline-none focus:border-ccc-plum">
            {TYPES.map(tp => <option key={tp} value={tp}>{tp === "All" ? t("statements.type_all") : tp}</option>)}
          </select>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {items.map(s => (
            <Link key={s.id} to={`/resources/statements/${s.slug}`} data-testid={`statement-${s.slug}`}
              className="bg-white border border-ccc-rule p-6 hover:border-ccc-plum transition-colors group">
              <div className="uppercase tracking-wider text-ccc-gold mb-2" style={{ fontSize: "13px", letterSpacing: "0.14em", fontWeight: 700 }}>
                {s.type} &middot; {s.published_at}
              </div>
              <h3 className="font-serif text-2xl text-ccc-plum group-hover:text-ccc-plum-deep leading-tight mb-3">{s.title}</h3>
              <p className="text-ccc-body line-clamp-3" style={{ fontSize: "16px" }}>{s.excerpt}</p>
              <div className="mt-3 text-ccc-muted" style={{ fontSize: "14px" }}>
                {t("statements.by_author")} {s.author}
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}

export function StatementDetail() {
  const { slug } = useParams();
  const t = useT();
  const { lang } = useLang();
  const [s, setS] = useState(null);
  useEffect(() => { getStatement(slug, lang).then(setS).catch(() => setS(false)); }, [slug, lang]);

  if (s === false) return <div className="max-w-4xl mx-auto px-6 py-24 font-serif text-2xl">{t("statements.not_found")}</div>;
  if (!s) return <div className="max-w-4xl mx-auto px-6 py-24">{t("common.loading")}</div>;

  return (
    <article className="max-w-3xl mx-auto px-6 py-16" data-testid="statement-detail">
      <ArticleJsonLd
        statement={s}
        canonicalUrl={typeof window !== "undefined" ? window.location.href : ""}
      />
      <Link to="/resources/statements" className="text-ccc-plum inline-flex items-center gap-1 mb-8 underline decoration-ccc-gold underline-offset-4" style={{ fontSize: "15px" }}>
        <ArrowLeft size={14}/> {t("statements.library_link")}
      </Link>
      <div className="uppercase tracking-wider text-ccc-gold mb-3" style={{ fontSize: "13px", letterSpacing: "0.14em", fontWeight: 700 }}>
        {s.type} &middot; {s.published_at}
      </div>
      <h1 className="font-serif text-5xl text-ccc-plum leading-tight mb-4">{s.title}</h1>
      <div className="text-ccc-muted mb-6" style={{ fontSize: "15px" }}>
        {t("statements.by_author")} {s.author}
      </div>
      <div className="gilded-divider mb-8"/>
      <div className="font-serif text-lg leading-relaxed text-ccc-ink whitespace-pre-wrap">{s.body}</div>
    </article>
  );
}
